import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Award,
  Sparkles,
  Compass,
  Target,
  Heart,
  Linkedin,
  ArrowRight,
  ArrowUpRight,
  GraduationCap,
  Quote,
  Trophy,
  Medal,
  Users,
  BookOpen,
  Zap,
  ShieldCheck,
  Lightbulb,
  Rocket,
  Star,
  ChevronRight,
  Mail,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { useCms } from "@/lib/cms";
import TrainersSection from "@/components/TrainersSection";
import TestimonialsSection from "@/components/TestimonialsSection";
import CountUp from "@/components/CountUp";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

const DEFAULT_TIMELINE = [
  { year: "2018", title: "Founded", body: "Educators, researchers, and product designers set out to reinvent teacher training." },
  { year: "2020", title: "First 100 schools", body: "Onboarded our first cohort of ambitious K-12 partners across three metros." },
  { year: "2023", title: "12,000 teachers", body: "Crossed 12,000 teachers trained with an average NPS of 62 across programs." },
  { year: "2026", title: "Enterprise platform", body: "Launched as an enterprise SaaS with certificates, RBAC, and reports." },
];

const DEFAULT_ACHIEVEMENTS = [
  { k: "12,400+", v: "Teachers trained" },
  { k: "212", v: "Partner schools" },
  { k: "34,700", v: "Certificates issued" },
  { k: "8", v: "Years of practice" },
  { k: "94%", v: "Would recommend" },
];

const DEFAULT_WHY_CHOOSE = [
  { icon: "GraduationCap", title: "Expert trainers", body: "Every trainer is vetted for classroom experience and research credentials." },
  { icon: "BookOpen", title: "Industry curriculum", body: "Modular programs designed with school leaders and curriculum specialists." },
  { icon: "ShieldCheck", title: "CBSE aligned", body: "Content mapped to CBSE, IB, and Cambridge frameworks by default." },
  { icon: "Lightbulb", title: "AI-powered practice", body: "Trainers integrate AI tools for grading, planning, and personalised feedback." },
  { icon: "Zap", title: "Practical workshops", body: "Every session ends with a classroom-ready artefact teachers can use tomorrow." },
  { icon: "Award", title: "QR-verified certificates", body: "Auto-generated PDFs with QR-backed public verification for every teacher." },
  { icon: "Rocket", title: "White-glove support", body: "Dedicated coordinator, live rescheduling, and one-business-day response times." },
];

const WHY_ICON_MAP = {
  GraduationCap,
  BookOpen,
  ShieldCheck,
  Lightbulb,
  Zap,
  Award,
  Rocket,
  Sparkles,
  Users,
  Heart,
  Trophy,
};

const resolveMedia = (url) => (url ? (url.startsWith("http") ? url : `${BACKEND}${url}`) : "");

export default function About() {
  const { about, home, site } = useCms();
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api
      .get("/gallery")
      .then((r) => {
        // Flatten albums -> photos (latest first)
        const albums = Array.isArray(r.data) ? r.data : [];
        const photos = albums.flatMap((a) =>
          (a.photos || []).map((p) => ({
            url: typeof p === "string" ? p : (p.url || p.path || ""),
            album_id: a.id,
            album_title: a.training_name || a.school_name,
          }))
        );
        setGallery(photos.slice(0, 8));
      })
      .catch(() => setGallery([]));
  }, []);

  // CMS-driven fields with sensible defaults
  const companyName = site?.company_name || "";
  const eyebrow = about?.eyebrow || `About ${companyName}`;
  const title = about?.title || "A quiet revolution in teacher training.";
  const body =
    about?.body ||
    `${companyName} is built by educators, researchers, and designers who believe teacher development deserves the same craft as the tools teachers use with students.`;
  const vision = about?.vision || "Every classroom, taught by a teacher who is still learning.";
  const mission =
    about?.mission ||
    "Design and deliver research-backed training that respects teacher time and elevates student outcomes.";
  const journey =
    about?.journey ||
    "From a two-person research studio to the platform behind 200+ schools, our journey has been about one thing — building the tools teachers wish they had.";
  const values =
    about?.values && about.values.length
      ? about.values
      : ["Craft over cleverness", "Evidence over opinion", "Teachers first, always"];
  const founder = about?.founder || {};
  const timeline = about?.timeline && about.timeline.length ? about.timeline : DEFAULT_TIMELINE;
  const achievements =
    about?.achievements && about.achievements.length ? about.achievements : DEFAULT_ACHIEVEMENTS;
  const whyChoose =
    about?.why_choose && about.why_choose.length ? about.why_choose : DEFAULT_WHY_CHOOSE;
  // Reused directly from the homepage's real CMS fields — same source of truth, not
  // duplicated data. Editing these in admin affects both pages, which is the correct
  // behavior for genuinely shared facts (stats, partner list) rather than inventing an
  // About-specific copy of the same information.
  const heroStats = home?.stats && home.stats.length ? home.stats.slice(0, 4) : [];
  const partners = home?.partners && home.partners.length ? home.partners : [
    "Delhi Public School",
    "Bombay Scottish",
    "St. Mary's",
    "Vidya Mandir",
    "Heritage School",
    "The Cathedral School",
    "Modern School",
    "Bishop Cotton",
  ];

  return (
    <div data-testid="about-page" style={{ backgroundColor: "#F8FAFC" }}>
      {/* HERO — light, matching the redesigned homepage */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(59,104,231,0.08),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(69,184,172,0.08),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-16">
          <motion.div
            variants={fadeUp} initial="hidden" animate="show" custom={0}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-6"
            style={{ backgroundColor: "#3B68E714", color: "#3B68E7" }}
          >
            <Sparkles className="w-3.5 h-3.5" aria-hidden="true" /> {eyebrow}
          </motion.div>
          <motion.h1
            variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="rebrand-serif tracking-tight font-bold leading-[1.05] max-w-4xl"
            style={{ fontSize: "clamp(2.5rem, 5vw + 1rem, 4rem)", color: "#1A202C" }}
            data-testid="about-headline"
          >
            {title.split(" ").slice(0, -2).join(" ")}{" "}
            <span style={{ color: "#3B68E7" }}>{title.split(" ").slice(-2).join(" ")}</span>
          </motion.h1>
          <motion.p
            variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed"
            style={{ color: "#64748B" }}
          >
            {body}
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/programs"
              className="inline-flex items-center gap-2 text-white text-sm font-semibold transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "12px 24px", boxShadow: "0 4px 12px rgba(59,104,231,0.3)" }}
            >
              Explore Programs <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-sm font-medium"
              style={{ border: "2px solid #DDA756", color: "#DDA756", borderRadius: 50, padding: "10px 22px" }}
            >
              Contact Us
            </Link>
          </motion.div>

          {/* Trust badges — real home.stats, not invented */}
          {heroStats.length > 0 && (
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
              {heroStats.map((s, i) => (
                <div key={i}>
                  <div className="rebrand-serif text-xl font-bold" style={{ color: "#1A202C" }}>{s.k}</div>
                  <div className="text-xs" style={{ color: "#64748B" }}>{s.v}</div>
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </section>


      {/* SECTION 1 — Founder / Director (Priority) */}
      {founder?.name && (
        <FounderSection founder={founder} companyName={companyName} />
      )}

      {/* SECTION 2 — Our Story: Mission + Vision + Journey + Values */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="about-story-section">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="max-w-2xl mb-14"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B68E7] mb-3">Our story</div>
          <h2 className="rebrand-serif text-4xl sm:text-5xl tracking-tight font-semibold">Why we build {companyName}.</h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StoryCard
            index={0}
            icon={Compass}
            eyebrow="Vision"
            eyebrowColor="text-[#3B68E7]"
            iconBg="bg-[#3B68E714] border-[#3B68E71A]"
            iconColor="text-[#3B68E7]"
            body={vision}
            testid="about-vision-card"
          />
          <StoryCard
            index={1}
            icon={Target}
            eyebrow="Mission"
            eyebrowColor="text-emerald-700"
            iconBg="bg-emerald-50 border-emerald-100"
            iconColor="text-emerald-600"
            body={mission}
            testid="about-mission-card"
          />
          <StoryCard
            index={2}
            icon={Rocket}
            eyebrow="Journey"
            eyebrowColor="text-amber-700"
            iconBg="bg-amber-50 border-amber-100"
            iconColor="text-amber-600"
            body={journey}
            testid="about-journey-card"
          />
          <StoryCard
            index={3}
            icon={Heart}
            eyebrow="Principles"
            eyebrowColor="text-rose-700"
            iconBg="bg-rose-50 border-rose-100"
            iconColor="text-rose-600"
            body={
              <div className="mt-2 flex flex-wrap gap-1.5">
                {values.slice(0, 6).map((v, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 text-[#374151] text-xs border border-[#E2E8F0]"
                  >
                    {v}
                  </span>
                ))}
              </div>
            }
            testid="about-values-card"
          />
        </div>
      </section>

      {/* SECTION 3 — Interactive Timeline */}
      <section className="bg-[#F1F5F9] border-y border-[#E2E8F0] py-24" data-testid="about-timeline-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="max-w-2xl mb-14"
          >
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B68E7] mb-3">Milestones</div>
            <h2 className="rebrand-serif text-4xl sm:text-5xl tracking-tight font-semibold">A journey worth walking.</h2>
          </motion.div>
          <div className="relative">
            <div className="absolute left-4 sm:left-1/2 top-2 bottom-2 w-px bg-gradient-to-b from-indigo-200 via-gray-200 to-emerald-200" />
            <div className="space-y-10">
              {timeline.map((t, i) => (
                <motion.div
                  key={`${t.year}-${i}`}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-40px" }}
                  custom={i}
                  className={`relative grid sm:grid-cols-2 gap-6 ${
                    i % 2 === 0 ? "" : "sm:[&>*:first-child]:col-start-2"
                  }`}
                  data-testid={`about-timeline-${t.year}`}
                >
                  <div
                    className={`pl-12 sm:pl-0 ${
                      i % 2 === 0 ? "sm:pr-12 sm:text-right" : "sm:pl-12"
                    }`}
                  >
                    <div className="text-xs font-mono uppercase tracking-wider text-[#3B68E7] font-semibold mb-1">
                      {t.year}
                    </div>
                    <div className="rebrand-serif text-2xl font-semibold text-[#1A202C]">{t.title}</div>
                    <p className="mt-2 text-sm text-[#64748B] leading-relaxed max-w-md sm:inline-block">
                      {t.body}
                    </p>
                  </div>
                  <div className="absolute left-4 sm:left-1/2 top-2 -translate-x-1/2 w-3 h-3 rounded-full bg-white border-2 border-[#3B68E7] shadow-[0_0_0_4px_rgba(79,70,229,0.15)]" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — Achievements (animated counters) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="about-achievements-section">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="max-w-2xl mb-14"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B68E7] mb-3">Impact</div>
          <h2 className="rebrand-serif text-4xl sm:text-5xl tracking-tight font-semibold">Numbers that matter.</h2>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {achievements.map((s, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-40px" }}
              custom={i}
              className="relative rounded-[24px] bg-white border border-[#E2E8F0] p-6 overflow-hidden hover:shadow-[0_14px_35px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 transition-all"
              data-testid={`about-stat-${i}`}
            >
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-gradient-to-br from-indigo-100 to-emerald-100 opacity-40 blur-2xl" />
              <div className="relative">
                <div className="rebrand-serif text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A202C]">
                  <CountUp value={s.k} />
                </div>
                <div className="mt-2 text-xs sm:text-sm text-[#64748B]">{s.v}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SECTION 5 — Our Team (CMS-driven) */}
      <TrainersSection
        eyebrow="The team"
        title="Trainers behind the platform."
        description={`Every trainer on ${companyName} is vetted for pedagogy craft, classroom impact, and long-form mentorship.`}
        variant="default"
        showFilters
        columns="md:grid-cols-2 lg:grid-cols-3"
        showContact
        testid="about-trainers-section"
      />

      {/* SECTION 6 — Why Choose Us */}
      <section className="bg-[#F1F5F9] border-y border-[#E2E8F0] py-24" data-testid="about-why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="max-w-2xl mb-14"
          >
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B68E7] mb-3">Why {companyName}</div>
            <h2 className="rebrand-serif text-4xl sm:text-5xl tracking-tight font-semibold">
              Seven reasons schools stay.
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((f, i) => {
              const Icon = WHY_ICON_MAP[f.icon] || Sparkles;
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-40px" }}
                  custom={i}
                  className="group rounded-[24px] bg-white border border-[#E2E8F0] p-6 hover:border-gray-300 hover:shadow-[0_14px_35px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 transition-all"
                  data-testid={`about-why-${i}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#3B68E714] border border-[#3B68E71A] grid place-items-center mb-4 group-hover:bg-indigo-100 transition-colors">
                    <Icon className="w-5 h-5 text-[#3B68E7]" strokeWidth={2} />
                  </div>
                  <h3 className="rebrand-serif text-xl font-medium text-[#1A202C] leading-snug">{f.title}</h3>
                  <p className="mt-2 text-sm text-[#64748B] leading-relaxed">{f.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 7 — Partners & Schools (from home.partners CMS) */}
      {partners.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="about-partners-section">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="max-w-2xl mb-10"
          >
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B68E7] mb-3">Partners</div>
            <h2 className="rebrand-serif text-4xl sm:text-5xl tracking-tight font-semibold">
              Trusted by ambitious schools.
            </h2>
          </motion.div>
          <div className="rounded-3xl border border-[#E2E8F0] bg-white overflow-hidden">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-px bg-gray-100">
              {partners.map((p, i) => (
                <motion.div
                  key={`${p}-${i}`}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-40px" }}
                  custom={i}
                  className="bg-white px-6 py-8 grid place-items-center hover:bg-gray-50 transition-colors group"
                  data-testid={`about-partner-${i}`}
                >
                  <div className="rebrand-serif text-lg font-medium text-gray-400 group-hover:text-[#1A202C] transition-colors text-center">
                    {p}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 8 — Testimonials (CMS-driven, reused component) */}
      <TestimonialsSection
        eyebrow="What educators say"
        title={`Principals and teachers on ${companyName}.`}
        description="Real voices from real classrooms."
        limit={6}
        tone="muted"
        testid="about-testimonials-section"
      />

      {/* SECTION 9 — Gallery Preview */}
      {gallery.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="about-gallery-section">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="flex items-end justify-between mb-10 flex-wrap gap-4"
          >
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B68E7] mb-3">From the field</div>
              <h2 className="rebrand-serif text-4xl sm:text-5xl tracking-tight font-semibold">Moments from our workshops.</h2>
            </div>
            <Link
              to="/gallery"
              data-testid="about-gallery-view-all"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3B68E7] hover:underline"
            >
              View gallery <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {gallery.map((p, i) => (
              <motion.div
                key={`${p.album_id}-${i}`}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-40px" }}
                custom={i}
                className={`relative rounded-xl overflow-hidden bg-gray-100 group ${
                  i % 5 === 0 ? "row-span-2 aspect-square md:aspect-[3/4]" : "aspect-square"
                }`}
                data-testid={`about-gallery-item-${i}`}
              >
                <Link to={`/gallery/${p.album_id}`}>
                  <img
                    src={resolveMedia(p.url)}
                    alt={p.album_title || "Gallery"}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  {p.album_title && (
                    <div className="absolute bottom-2 left-2 right-2 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity truncate">
                      {p.album_title}
                    </div>
                  )}
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* SECTION 10 — Final CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pb-24">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="relative overflow-hidden p-10 sm:p-16"
          style={{ borderRadius: 32, background: "linear-gradient(135deg, #3B68E7 0%, #2947B0 100%)" }}
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.12),transparent_55%)]" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center text-white">
            <div>
              <Sparkles className="w-6 h-6 mb-4" style={{ color: "#DDA756" }} aria-hidden="true" />
              <h2 className="rebrand-serif text-3xl sm:text-5xl tracking-tight font-bold">
                Ready to transform teaching in your school?
              </h2>
              <p className="mt-4 text-white/80 max-w-md">
                Explore programs, book a discovery call, or drop us a message — we&apos;ll design a training arc for
                you in 48 hours.
              </p>
            </div>
            <div className="md:justify-self-end flex flex-wrap gap-3">
              <Link
                to="/programs" data-testid="about-cta-programs"
                className="border border-white/30 text-white hover:bg-white/10 text-sm font-medium px-5 py-3 rounded-full inline-flex items-center gap-2 transition-colors"
              >
                Explore programs
              </Link>
              <Link
                to="/book" data-testid="about-cta-book"
                className="bg-white hover:bg-white/90 text-sm font-semibold px-5 py-3 rounded-full inline-flex items-center gap-2 group transition-transform hover:scale-[1.02]"
                style={{ color: "#3B68E7" }}
              >
                Book a Training <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
              </Link>
              <Link
                to="/contact" data-testid="about-cta-contact"
                className="border border-white/30 text-white hover:bg-white/10 text-sm font-medium px-5 py-3 rounded-full inline-flex items-center gap-2 transition-colors"
              >
                Contact Our Team <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}

/* ------------------------ SECTION COMPONENTS ------------------------ */

function StoryCard({ index, icon: Icon, eyebrow, eyebrowColor, iconBg, iconColor, body, testid }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-40px" }}
      custom={index}
      whileHover={{ y: -3 }}
      className="rounded-[24px] border border-[#E2E8F0] bg-white p-6 hover:shadow-[0_14px_35px_rgba(0,0,0,0.08)] transition-all h-full"
      data-testid={testid}
    >
      <div className={`w-10 h-10 rounded-lg border grid place-items-center mb-4 ${iconBg}`}>
        <Icon className={`w-5 h-5 ${iconColor}`} strokeWidth={2} />
      </div>
      <div className={`text-xs uppercase tracking-wider font-semibold ${eyebrowColor}`}>{eyebrow}</div>
      {typeof body === "string" ? (
        <p className="mt-2 text-[15px] rebrand-serif leading-snug text-[#1A202C]">{body}</p>
      ) : (
        body
      )}
    </motion.div>
  );
}

function FounderSection({ founder, companyName }) {
  const {
    name,
    designation,
    bio,
    image,
    linkedin,
    email,
    personal_message,
    signature,
    experience,
    specialization,
    qualifications,
    awards,
    achievements,
  } = founder;
  const socialLinks = founder.social || {};

  return (
    <section
      className="relative overflow-hidden bg-white py-24"
      data-testid="about-founder-section"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-24 left-1/4 w-96 h-96 rounded-full bg-gradient-to-br from-indigo-100 to-transparent blur-3xl opacity-60" />
        <div className="absolute bottom-24 right-1/4 w-96 h-96 rounded-full bg-gradient-to-br from-emerald-100 to-transparent blur-3xl opacity-60" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="max-w-2xl mb-14"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B68E7] mb-3">
            Meet the founder
          </div>
          <h2 className="rebrand-serif text-4xl sm:text-5xl tracking-tight font-semibold">
            The person behind {companyName}.
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-[minmax(0,420px),1fr] gap-10 lg:gap-16 items-start">
          {/* Founder image + floating cards */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
            data-testid="about-founder-image"
          >
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] bg-gradient-to-br from-indigo-100 via-white to-emerald-100 shadow-2xl shadow-indigo-900/10 ring-1 ring-black/5">
              {image ? (
                <motion.img
                  src={resolveMedia(image)}
                  alt={name}
                  className="w-full h-full object-cover"
                  initial={{ scale: 1.05 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                />
              ) : (
                <div className="w-full h-full grid place-items-center rebrand-serif text-8xl font-semibold text-[#1A202C]/25">
                  {(name || "?").charAt(0)}
                </div>
              )}
            </div>

            {/* Floating experience card — hidden on mobile to prevent clipping at narrow widths */}
            {experience && (
              <motion.div
                initial={{ opacity: 0, y: 20, rotate: -6 }}
                whileInView={{ opacity: 1, y: 0, rotate: -6 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="hidden sm:block absolute -bottom-6 -left-6 w-52 rounded-[24px] border border-white/60 bg-white/85 backdrop-blur-xl p-4 shadow-2xl"
              >
                <div className="flex items-center gap-2 text-xs font-semibold text-[#3B68E7] mb-1">
                  <Medal className="w-3.5 h-3.5" /> Experience
                </div>
                <div className="rebrand-serif text-2xl font-semibold text-[#1A202C]">{experience}</div>
                {specialization && (
                  <div className="mt-1 text-xs text-[#64748B] leading-tight">{specialization}</div>
                )}
              </motion.div>
            )}

            {/* Floating achievements card — hidden on mobile to prevent clipping at narrow widths */}
            {Array.isArray(achievements) && achievements.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20, rotate: 4 }}
                whileInView={{ opacity: 1, y: 0, rotate: 4 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="hidden sm:block absolute -top-4 -right-4 w-56 rounded-[24px] border border-white/60 bg-white/85 backdrop-blur-xl p-4 shadow-2xl"
              >
                <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 mb-2">
                  <Trophy className="w-3.5 h-3.5" /> Achievements
                </div>
                <div className="text-xs text-[#374151] line-clamp-3 leading-relaxed">
                  {achievements[0]}
                </div>
                {achievements.length > 1 && (
                  <div className="mt-1 text-[10px] font-medium text-gray-400">
                    + {achievements.length - 1} more
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>

          {/* Founder info */}
          <div>
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#3B68E71A] bg-[#3B68E714] text-xs font-semibold text-[#3B68E7] mb-4">
                <Award className="w-3.5 h-3.5" /> Founder &amp; Director
              </div>
              <h3
                className="rebrand-serif text-4xl sm:text-5xl font-semibold tracking-tight text-[#1A202C]"
                data-testid="about-founder-name"
              >
                {name}
              </h3>
              <div className="mt-2 text-lg text-[#64748B]">
                {[designation, companyName].filter(Boolean).join(" · ")}
              </div>
            </motion.div>

            {/* Personal message quote */}
            {personal_message && (
              <motion.blockquote
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                custom={1}
                className="mt-8 relative rounded-[24px] bg-gradient-to-br from-[#EEF2FF] via-white to-emerald-50 border border-[#3B68E71A] p-6 sm:p-7"
                data-testid="about-founder-message"
              >
                <Quote className="absolute -top-3 -left-3 w-8 h-8 text-[#3B68E7] bg-white rounded-full p-1.5 border border-[#3B68E71A]" />
                <p className="text-[17px] leading-relaxed text-[#1A202C] rebrand-serif italic">
                  &ldquo;{personal_message}&rdquo;
                </p>
                {signature && (
                  <div className="mt-4 rebrand-serif italic text-lg text-[#3B68E7]">— {signature}</div>
                )}
              </motion.blockquote>
            )}

            {/* Biography */}
            {bio && (
              <motion.p
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                custom={2}
                className="mt-8 text-[#374151] leading-relaxed text-[15px]"
                data-testid="about-founder-bio"
              >
                {bio}
              </motion.p>
            )}

            {/* Meta grid */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={3}
              className="mt-8 grid sm:grid-cols-2 gap-4"
            >
              {qualifications?.length > 0 && (
                <MetaBlock
                  icon={GraduationCap}
                  label="Qualifications"
                  items={qualifications}
                  testid="about-founder-qualifications"
                />
              )}
              {Array.isArray(awards) && awards.length > 0 && (
                <MetaBlock
                  icon={Trophy}
                  label="Awards"
                  items={awards}
                  testid="about-founder-awards"
                  tone="amber"
                />
              )}
            </motion.div>

            {/* Vision statement */}
            {founder.vision_statement && (
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                custom={4}
                className="mt-8 rounded-[24px] border border-[#E2E8F0] bg-white p-6"
              >
                <div className="text-xs uppercase tracking-wider font-semibold text-[#3B68E7] mb-2 flex items-center gap-1.5">
                  <Compass className="w-3.5 h-3.5" /> My vision
                </div>
                <p className="text-[#374151] leading-relaxed">{founder.vision_statement}</p>
              </motion.div>
            )}

            {/* Actions */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={5}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              {email && (
                <a
                  href={`mailto:${email}`}
                  data-testid="about-founder-contact"
                  className="inline-flex items-center gap-2 bg-[#050914] hover:bg-[#1A202C] text-white text-sm font-semibold px-5 py-3 rounded-full transition-all group"
                >
                  <Mail className="w-4 h-4" /> Reach out
                  <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </a>
              )}
              {linkedin && (
                <a
                  href={linkedin}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="about-founder-linkedin"
                  className="inline-flex items-center gap-1.5 border border-[#E2E8F0] hover:border-gray-400 text-[#1A202C] text-sm font-medium px-4 py-2.5 rounded-full transition-all"
                >
                  <Linkedin className="w-4 h-4" /> LinkedIn
                </a>
              )}
              {socialLinks.twitter && (
                <a
                  href={socialLinks.twitter}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 border border-[#E2E8F0] hover:border-gray-400 text-[#1A202C] text-sm font-medium px-4 py-2.5 rounded-full transition-all"
                >
                  Twitter
                </a>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MetaBlock({ icon: Icon, label, items, testid, tone = "indigo" }) {
  const chipCls =
    tone === "amber"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : "bg-[#3B68E714] text-[#3B68E7] border-[#3B68E71A]";
  return (
    <div className="rounded-[24px] border border-[#E2E8F0] bg-white p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider font-semibold text-[#64748B] flex items-center gap-1.5 mb-3">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((q, i) => (
          <span
            key={i}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-medium ${chipCls}`}
          >
            {q}
          </span>
        ))}
      </div>
    </div>
  );
}
